import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import KairosLogo from '../../components/KairosLogo';
import { supabase } from '../../lib/supabase';
import './AuthPage.css';

export default function AuthPage({ onGuest, onAuth, onSignUp, authError, signIn, signUp, loading, initialMode = 'signin', initialEmail = '' }) {
  const { t } = useTranslation();
  const [mode,     setMode]     = useState(initialMode); // 'signin' | 'signup' | 'confirm' | 'forgot' | 'forgot-sent'
  const [email,    setEmail]    = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [busy,     setBusy]     = useState(false);
  const [localErr, setLocalErr] = useState('');
  const [resent,   setResent]   = useState(false);
  const [showPwd,  setShowPwd]  = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [codeMsg,  setCodeMsg]  = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const navigate = useNavigate();
  const error = localErr || authError || '';

  async function handleRedeemCode(e) {
    e.preventDefault();
    const trimmed = accessCode.trim().toUpperCase();
    if (!trimmed) { setCodeMsg(t('codes.enterCode')); return; }
    setCodeBusy(true);
    setCodeMsg('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setCodeMsg(t('codes.signInFirst'));
        setCodeBusy(false);
        return;
      }
      const res = await fetch('/api/billing/codes/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await res.json();
      if (data.ok) {
        setCodeMsg(`✓ ${data.message}`);
        setAccessCode('');
        setTimeout(() => onAuth(), 1500);
      } else {
        setCodeMsg(data.error || t('codes.invalidCode'));
      }
    } catch {
      setCodeMsg(t('codes.redeemError'));
    }
    setCodeBusy(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLocalErr('');
    if (!email.trim()) { setLocalErr(t('auth.emailRequired')); return; }
    if (password.length < 6) { setLocalErr(t('auth.passwordTooShort')); return; }
    setBusy(true);
    if (mode === 'signin') {
      const ok = await signIn(email.trim(), password);
      if (ok) onAuth();
    } else {
      const result = await signUp(email.trim(), password);
      if (result?.ok) {
        (onSignUp || onAuth)();
      } else if (result?.needsConfirm) {
        // Email confirmation required — show the check-your-email screen
        setMode('confirm');
      } else if (result?.duplicate) {
        // Switch to sign-in mode with the email pre-filled — they likely
        // already have an account
        setMode('signin');
      }
    }
    setBusy(false);
  }

  // ── Forgot password — success screen ────────────────────────────────────
  if (mode === 'forgot-sent') {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-row">
            <KairosLogo size={52} color="#6a7662" />
          </div>
          <h1 className="auth-app-name">Kairos</h1>
          <div className="auth-confirm-wrap">
            <div className="auth-confirm-icon">✉</div>
            <h2 className="auth-confirm-title">{t('auth.resetSent')}</h2>
            <p className="auth-confirm-text">{t('auth.resetSentDesc', { email: forgotEmail })}</p>
            <p className="auth-confirm-hint">{t('auth.checkSpam')}</p>
            <div className="auth-confirm-actions">
              <button className="auth-btn-secondary" onClick={() => { setMode('signin'); setLocalErr(''); }} type="button">
                {t('auth.backToSignIn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Forgot password — email entry screen ─────────────────────────────────
  if (mode === 'forgot') {
    async function handleForgotSubmit(e) {
      e.preventDefault();
      const trimmed = forgotEmail.trim();
      if (!trimmed) { setLocalErr(t('auth.emailRequired')); return; }
      setForgotBusy(true);
      setLocalErr('');
      await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${window.location.origin}/reset-password`,
        // Falls back to origin if /reset-password isn't in Supabase allowlist;
        // App.jsx detects #type=recovery hash and routes to ResetPasswordPage
      });
      // Always show success — never reveal whether the email exists
      setForgotBusy(false);
      setMode('forgot-sent');
    }
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-row">
            <KairosLogo size={52} color="#6a7662" />
          </div>
          <h1 className="auth-app-name">Kairos</h1>
          <div className="auth-confirm-wrap">
            <div className="auth-confirm-icon">🔑</div>
            <h2 className="auth-confirm-title">{t('auth.forgotTitle')}</h2>
            <p className="auth-confirm-text">{t('auth.forgotDesc')}</p>
            <form className="auth-form" onSubmit={handleForgotSubmit} style={{ width: '100%', marginTop: '1rem' }} noValidate>
              <label className="auth-label">
                {t('auth.emailLabel')}
                <input
                  className="auth-input"
                  type="email"
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={e => { setForgotEmail(e.target.value); setLocalErr(''); }}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                />
              </label>
              {localErr && <p className="auth-error">{localErr}</p>}
              <button className="auth-btn-primary" type="submit" disabled={forgotBusy}>
                {forgotBusy ? t('auth.pleaseWait') : t('auth.sendResetLink')}
              </button>
            </form>
            <div className="auth-confirm-actions" style={{ marginTop: '1rem' }}>
              <button className="auth-btn-secondary" onClick={() => { setMode('signin'); setLocalErr(''); }} type="button">
                {t('auth.backToSignIn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Email confirmation screen (unchanged) ────────────────────────────────
  if (mode === 'confirm') {
    async function resendEmail() {
      setResent(false);
      await supabase.auth.resend({ type: 'signup', email });
      setResent(true);
    }
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-logo-row">
            <KairosLogo size={52} color="#6a7662" />
          </div>
          <h1 className="auth-app-name">Kairos</h1>
          <div className="auth-confirm-wrap">
            <div className="auth-confirm-icon">✉</div>
            <h2 className="auth-confirm-title">{t('auth.checkEmail')}</h2>
            <p className="auth-confirm-text">
              {t('auth.confirmSent', { email })}
            </p>
            <p className="auth-confirm-hint">
              {t('auth.checkSpam')}
            </p>
            {resent && <p className="auth-confirm-sent">{t('auth.emailResent')}</p>}
            <div className="auth-confirm-actions">
              <button className="auth-btn-secondary" onClick={resendEmail} type="button">
                {t('auth.resendEmail')}
              </button>
              <button className="auth-btn-secondary" onClick={() => setMode('signin')} type="button">
                {t('auth.backToSignIn')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full landing page ────────────────────────────────────────────────────
  return (
    <div className="landing-page">

      {/* ── Hero ── */}
      <header className="landing-hero">
        <div className="landing-hero-inner">
          <KairosLogo size={52} color="#6a7662" />
          <h1 className="landing-title">Kairos</h1>
          <p className="landing-subtitle">
            {t('auth.description')}
          </p>
        </div>
      </header>

      {/* ── Feature sections ── */}
      <section className="landing-features">

        {/* Bible Reading */}
        <div className="landing-feature-block">
          <p className="landing-feature-label">{t('landing.personalReading')}</p>
          <h2 className="landing-feature-heading">{t('landing.fourWays')}</h2>
          <p className="landing-feature-desc">
            {t('landing.fourWaysDesc')}
          </p>
          <div className="landing-path-grid">
            <div className="landing-path-item">
              <span className="landing-path-name">{t('nav.daily')}</span>
              <span className="landing-path-desc">{t('landing.dailyDesc')}</span>
            </div>
            <div className="landing-path-item">
              <span className="landing-path-name">{t('nav.journey')}</span>
              <span className="landing-path-desc">{t('landing.journeyDesc')}</span>
            </div>
            <div className="landing-path-item">
              <span className="landing-path-name">{t('nav.choose')}</span>
              <span className="landing-path-desc">{t('landing.chooseDesc')}</span>
            </div>
            <div className="landing-path-item">
              <span className="landing-path-name">{t('nav.lectionary')}</span>
              <span className="landing-path-desc">{t('landing.lectionaryDesc')}</span>
            </div>
          </div>
        </div>

        <div className="landing-divider-rule" />

        {/* Koinonia Groups */}
        <div className="landing-feature-block">
          <p className="landing-feature-label">{t('landing.churchCommunity')}</p>
          <h2 className="landing-feature-heading">{t('landing.koinoniaGroups')}</h2>
          <p className="landing-feature-desc">
            {t('landing.koinoniaDesc')}
          </p>
          <div className="landing-roles-grid">
            <div className="landing-role-item">
              <span className="landing-role-name">{t('landing.pastors')}</span>
              <span className="landing-role-desc">{t('landing.pastorsDesc')}</span>
            </div>
            <div className="landing-role-item">
              <span className="landing-role-name">{t('landing.coordinators')}</span>
              <span className="landing-role-desc">{t('landing.coordinatorsDesc')}</span>
            </div>
            <div className="landing-role-item">
              <span className="landing-role-name">{t('landing.leaders')}</span>
              <span className="landing-role-desc">{t('landing.leadersDesc')}</span>
            </div>
            <div className="landing-role-item">
              <span className="landing-role-name">{t('landing.members')}</span>
              <span className="landing-role-desc">{t('landing.membersDesc')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sign-in card ── */}
      <section className="landing-auth-section">
        <div className="auth-card">
          <div className="auth-logo-row">
            <KairosLogo size={40} color="#6a7662" />
          </div>
          <h2 className="auth-app-name">{t('auth.heading')}</h2>
          <p className="auth-tagline">{t('auth.subheading')}</p>

          <div className="auth-tabs">
            <button
              className={`auth-tab${mode === 'signin' ? ' active' : ''}`}
              onClick={() => { setMode('signin'); setLocalErr(''); }}
            >
              {t('auth.tabSignIn')}
            </button>
            <button
              className={`auth-tab${mode === 'signup' ? ' active' : ''}`}
              onClick={() => { setMode('signup'); setLocalErr(''); }}
            >
              {t('auth.tabCreate')}
            </button>
          </div>

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <label className="auth-label">
              {t('auth.emailLabel')}
              <input
                className="auth-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setLocalErr(''); }}
                placeholder={t('auth.emailPlaceholder')}
                required
              />
            </label>
            <label className="auth-label">
              {t('auth.passwordLabel')}
              <div className="auth-pwd-wrap">
                <input
                  className="auth-input"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={e => { setPassword(e.target.value); setLocalErr(''); }}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  className="auth-pwd-toggle"
                  onClick={() => setShowPwd(s => !s)}
                  aria-label={showPwd ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPwd ? t('auth.hidePassword') : t('auth.showPassword')}
                </button>
              </div>
            </label>

            {mode === 'signin' && (
              <button
                type="button"
                className="auth-forgot-link"
                onClick={() => { setForgotEmail(email); setLocalErr(''); setMode('forgot'); }}
              >
                {t('auth.forgotPassword')}
              </button>
            )}

            {error && <p className="auth-error">{error}</p>}

            <button
              className="auth-btn-primary"
              type="submit"
              disabled={busy || loading}
            >
              {busy ? t('auth.pleaseWait') : mode === 'signin' ? t('auth.tabSignIn') : t('auth.tabCreate')}
            </button>
          </form>

          {mode === 'signup' && (
            <ul className="auth-perks">
              <li>{t('auth.signUpBenefits.save')}</li>
              <li>{t('auth.signUpBenefits.notes')}</li>
              <li>{t('auth.signUpBenefits.join')}</li>
            </ul>
          )}

          <div className="auth-divider"><span>{t('auth.or')}</span></div>
          <button className="auth-guest-btn" onClick={onGuest} type="button">
            {t('auth.continueAsGuest')}
          </button>
          <p className="auth-guest-note">
            {t('auth.guestNote')}
          </p>

          {/* ── Enter Access Code ── */}
          <div className="auth-divider"><span>{t('codes.haveCode', 'Have a code?')}</span></div>
          <button
            className="auth-guest-btn"
            onClick={() => setShowCode(s => !s)}
            type="button"
            style={{ fontSize: '0.85rem' }}
          >
            🎁 {t('codes.enterCodeBtn', 'Enter Access Code')}
          </button>
          {showCode && (
            <form className="auth-code-form" onSubmit={handleRedeemCode} style={{ marginTop: '0.75rem' }}>
              <input
                className="auth-input"
                type="text"
                value={accessCode}
                onChange={e => { setAccessCode(e.target.value.toUpperCase()); setCodeMsg(''); }}
                placeholder={t('codes.placeholder', 'KAIROS-XXXX-XXXX')}
                maxLength={20}
                style={{ textAlign: 'center', letterSpacing: '0.08em', fontFamily: 'monospace' }}
              />
              <button
                className="auth-btn-primary"
                type="submit"
                disabled={codeBusy || !accessCode.trim()}
                style={{ marginTop: '0.5rem' }}
              >
                {codeBusy ? t('codes.redeeming', 'Redeeming…') : t('codes.redeem', 'Redeem Code')}
              </button>
              {codeMsg && (
                <p style={{ fontSize: '0.82rem', marginTop: '0.4rem', color: codeMsg.startsWith('✓') ? '#16a34a' : '#dc2626', textAlign: 'center' }}>
                  {codeMsg}
                </p>
              )}
            </form>
          )}
        </div>
      </section>

      <footer className="landing-footer">
        {t('app.tagline')}
      </footer>
    </div>
  );
}
